import { Database, ListChecks, Play } from "lucide-react";
import type { ConfigServerOverview } from "@/api/config-server-stages";
import type { TFunction } from "i18next";

/** Frase da etapa corrente, visível junto à barra e no aria-valuetext. */
export function stageCaption(overview: ConfigServerOverview, t: TFunction): string {
  const { extraction, management, execution } = overview.stages;
  if (extraction.state === "in_progress") return t("Extraction: domain {{done}} of {{total}}", {
    done: extraction.domains_done, total: extraction.domains_total,
  });
  if (extraction.state !== "done") return t("Extraction not run yet");
  if (management.state !== "done") return t("Management: {{decided}} of {{total}} items decided", {
    decided: management.decided, total: management.total,
  });
  if (execution.state === "done") return t("All stages completed");
  return t("Execution: {{completed}} of {{approved}} executed", {
    completed: execution.completed, approved: execution.approved,
  });
}

/** Etapa corrente para a legenda da barra: "Step 2 of 3 · Management". */
export function stageStepLabel(overview: ConfigServerOverview, t: TFunction): string {
  const { extraction, management } = overview.stages;
  const step = extraction.state !== "done" ? 1 : management.state !== "done" ? 2 : 3;
  const name = [t("Extraction"), t("Management"), t("Execution")][step - 1];
  return t("Step {{step}} of 3 · {{name}}", { step, name });
}

export function stageCardProps(overview: ConfigServerOverview, projectId: string, t: TFunction) {
  const base = `/projects/${projectId}/agents/configuration-server`;
  const { extraction, management, execution } = overview.stages;
  // Depois do último domínio ainda há publicação: não existe domínio 4 de 3.
  const fetching = extraction.state === "in_progress" && extraction.domains_done < extraction.domains_total;
  const extracted = extraction.state === "done" || extraction.state === "in_progress";
  const number = (value: number) => value.toLocaleString("en-US");
  return [
    {
      number: "01" as const, title: t("Extraction"),
      description: t("Reads the configuration from the source ERP."), state: extraction.state,
      value: extraction.value,
      metrics: !fetching && extracted ? [{
        label: t("Tables read"), value: number(extraction.tables_read),
        suffix: t("of {{total}} tables read", { total: number(extraction.tables_total) }),
      }] : undefined,
      note: fetching ? t("Fetching domain {{done}} of {{total}}", {
        done: extraction.domains_done + 1, total: extraction.domains_total,
      }) : t("Not run yet"),
      lockedReason: t("Approve the project scope first"), actionLabel: t("Run extraction"),
      actionIcon: Database, to: `${base}/extraction`,
    },
    {
      number: "02" as const, title: t("Management"),
      description: t("Approve or take out of scope each item."), state: management.state,
      value: management.value,
      metrics: [
        { label: t("Pending"), value: number(management.pending), tone: "warn" as const },
        { label: t("Decided"), value: number(management.decided), tone: "success" as const },
      ],
      progressLabel: t("{{decided}} of {{total}} items decided", {
        decided: number(management.decided), total: number(management.total),
      }),
      lockedReason: t("Unlocks when the extraction finishes"),
      actionLabel: management.state === "done" ? t("Open") : t("Continue"),
      actionIcon: ListChecks, to: `${base}/management`,
    },
    {
      number: "03" as const, title: t("Execution"),
      description: t("Configures the approved items in S/4HANA Cloud Public Edition."), state: execution.state,
      value: execution.value,
      metrics: execution.approved === 0 ? undefined : [{
        label: t("Executed"), value: number(execution.completed),
        suffix: t("of {{total}} approved", { total: number(execution.approved) }),
      }],
      note: t("Nothing to execute"),
      // Sem aprovados não há o que medir: a barra some e o painel já diz "Nothing to execute".
      progressLabel: execution.state !== "locked" && execution.approved === 0
        ? undefined
        : t("{{count}} items configured", { count: execution.completed }),
      // Abre com pelo menos uma aprovada, em qualquer aba (spec 2026-10-26, D16).
      lockedHint: t("Execution starts once at least one item is approved in Management."),
      lockedReason: extraction.state !== "done" ? t("Unlocks when the extraction finishes")
        : t("Waiting for at least one approved item"),
      actionLabel: t("Open"), actionIcon: Play, to: `${base}/execution`,
    },
  ];
}

/**
 * Por que nenhum ▶ da Execução pode rodar agora: só uma execução por projeto
 * (extração, cadastro no CBC ou conversa). `null` quando nada está em voo.
 */
export function runBlockedReason(overview: ConfigServerOverview | undefined, t: TFunction): string | null {
  if (!overview?.active_run) return null;
  return overview.active_run.kind === "extraction"
    ? t("Wait for the extraction to finish")
    : t("Another configuration is running");
}
